import { BuildView } from "~/components/build/Show/lib/types/BuildView";
import { Step } from "~/components/build/Show/lib/types";
import { BuildSearchParams } from "~/components/build/Show/lib/searchParams";
import { parseHash } from "~/stores/BuildPageStore";

export const DRAWER_PARAMS = ["jid", "sid", "open", "tab"] as const;

/**
 * Returns a copy of the given search params with all drawer-specific params removed.
 */
export function withoutDrawerParams(searchParams: URLSearchParams): URLSearchParams {
  const params = new URLSearchParams(searchParams);
  DRAWER_PARAMS.forEach((param) => params.delete(param));
  return params;
}

export function urlForView(buildPath: string, view: BuildView, params?: BuildSearchParams): string {
  const views: Record<BuildView, string> = {
    [BuildView.Index]: "",
    [BuildView.Summary]: "/summary",
    [BuildView.StepUploads]: "/summary/step-uploads",
    [BuildView.Canvas]: "/canvas",
    [BuildView.Table]: "/table",
    [BuildView.List]: "/list",
    [BuildView.Waterfall]: "/waterfall",
    [BuildView.Tests]: "/tests",
    [BuildView.Annotations]: "/annotations",
  };

  if (views[view] == null) {
    throw new Error(`Unknown view: ${view}`);
  }

  const pathname = `${buildPath}${views[view]}`;

  if (params) {
    return applySearchParams(pathname, params);
  }

  return pathname;
}

export function applySearchParams(path: string, params: BuildSearchParams, base?: URLSearchParams) {
  const searchParams = base ? new URLSearchParams(base) : new URLSearchParams();

  for (const [key, value] of Object.entries(params)) {
    if (value != null) {
      searchParams.set(key, value);
    }
  }

  const qs = searchParams.toString();
  return qs ? `${path}?${qs}` : path;
}

/**
 * Build a URL that sets `newParams` as the drawer params while preserving
 * any non-drawer params already in `currentSearch` (e.g. `annotations_tab`).
 */
export function preserveNonDrawerParams(
  path: string,
  currentSearch: string | URLSearchParams,
  newParams: BuildSearchParams,
): string {
  const base = withoutDrawerParams(
    typeof currentSearch === "string" ? new URLSearchParams(currentSearch) : currentSearch,
  );
  return applySearchParams(path, newParams, base);
}

/**
 * Resolve a job URL based on the current location or a custom pathname.
 *
 * @param jobUuid - The UUID of the job to resolve.
 * @param options - Options for resolving the URL.
 * @param options.pathname - The base path to use for the URL. Defaults to the current window location.
 * @param options.open - Whether to open the drawer for the job. Defaults to true.
 */
export function resolveJobUrl(
  jobUuid: string,
  { pathname = window.location.pathname, open = true } = {},
) {
  return applySearchParams(pathname, {
    jid: jobUuid,
    open: open === false ? "false" : undefined,
  });
}

/**
 * Resolve a step URL based on the current location or a custom pathname.
 *
 * @param stepUuid - The UUID of the step to resolve.
 * @param options - Options for resolving the URL.
 * @param options.pathname - The base path to use for the URL. Defaults to the current window location.
 * @param options.open - Whether to open the drawer for the step. Defaults to true.
 */
export function resolveStepUrl(
  stepUuid: Step["uuid"],
  { pathname = window.location.pathname, open = true } = {},
) {
  return applySearchParams(pathname, {
    sid: stepUuid,
    open: open === false ? "false" : undefined,
  });
}

/**
 * Convert classic URL fragments to modern (BBUI) search params and hash.
 *
 * Classic URLs encode the active job as a hash fragment: `#job-uuid/22-25`.
 * Modern URLs use query params (`?jid=job-uuid`) and a simplified hash
 * for log line references: `#/group-line`, `#/Lline`, or `#/group`.
 */
export function convertClassicToModernParams(
  search: string,
  hash: string,
): { search: string; hash: string } {
  const params = new URLSearchParams(search);
  const parsed = parseHash(hash, params.get("jid"));
  if (!parsed?.job) {
    return { search, hash };
  }

  params.set("jid", parsed.job);

  let modernHash = "";
  if (parsed.endLine && parsed.line) {
    modernHash = `#${parsed.job}/L${parsed.line}-L${parsed.endLine}`;
  } else if (parsed.line && parsed.group) {
    modernHash = `#${parsed.job}/${parsed.group}-${parsed.line}`;
  } else if (parsed.line && !parsed.group) {
    modernHash = `#${parsed.job}/L${parsed.line}`;
  } else if (parsed.group) {
    modernHash = `#${parsed.job}/${parsed.group}`;
  }

  return { search: `?${params.toString()}`, hash: modernHash };
}

/**
 * Convert modern (BBUI) search params to classic hash fragments.
 *
 * Strips `jid` from search params and encodes it as a hash fragment
 * (`#job-uuid/22-25`), preserving any log line references from the
 * existing hash.
 */
export function convertModernToClassicParams(
  search: string,
  hash: string,
): { search: string; hash: string } {
  const params = new URLSearchParams(search);
  const jid = params.get("jid");

  if (!jid) {
    return { search, hash };
  }

  params.delete("jid");
  params.delete("open");
  params.delete("tab");

  const parsed = parseHash(hash, jid);
  let lineRange = "";
  if (parsed?.endLine && parsed?.line) {
    lineRange = `/L${parsed.line}-L${parsed.endLine}`;
  } else if (parsed?.group && parsed?.line) {
    lineRange = `/${parsed.group}-${parsed.line}`;
  } else if (parsed?.line && !parsed?.group) {
    lineRange = `/L${parsed.line}`;
  } else if (parsed?.group) {
    lineRange = `/${parsed.group}`;
  }

  const qs = params.toString();
  return {
    search: qs ? `?${qs}` : "",
    hash: `#${jid}${lineRange}`,
  };
}
